import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE } from "@/data/site";
import { getServices } from "@/data/services";
import { getAreas } from "@/data/areas";
import { useLang } from "@/lib/i18n";

// Injects/refreshes schema.org JSON-LD on route change.
// - LocalBusiness for the site itself
// - Service entries for each plumbing service
// - Place entries for each service area
export default function StructuredData() {
  const { pathname } = useLocation();
  const { lang } = useLang();

  useEffect(() => {
    const origin = window.location.origin;
    const SERVICES = getServices(lang);
    const AREAS = getAreas(lang);

    const localBusiness = {
      "@context": "https://schema.org",
      "@type": "Plumber",
      "@id": `${origin}/#business`,
      name: SITE.name,
      url: origin,
      telephone: SITE.primaryPhone,
      email: SITE.email,
      image: SITE.logo,
      priceRange: "$$",
      address: {
        "@type": "PostalAddress",
        streetAddress: "1961 10th Ave N",
        addressLocality: "Lake Worth Beach",
        addressRegion: "FL",
        postalCode: "33461",
        addressCountry: "US",
      },
      geo: { "@type": "GeoCoordinates", latitude: 26.6168, longitude: -80.0728 },
      openingHoursSpecification: [{
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
        opens: "00:00",
        closes: "23:59",
      }],
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: SITE.rating.toFixed(1),
        reviewCount: String(SITE.reviewCount),
      },
      areaServed: AREAS.map((a) => ({
        "@type": "AdministrativeArea",
        name: a.name,
      })),
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Plumbing Services",
        itemListElement: SERVICES.map((s) => ({
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: s.name, url: `${origin}/services/${s.slug}` },
        })),
      },
      sameAs: [SITE.googleReviewsUrl],
    };

    const blocks = [localBusiness];

    // Page-specific schema
    if (pathname.startsWith("/services/")) {
      const slug = pathname.split("/").pop();
      const svc = SERVICES.find((s) => s.slug === slug);
      if (svc) {
        blocks.push({
          "@context": "https://schema.org",
          "@type": "Service",
          name: svc.name,
          description: svc.summary,
          serviceType: svc.name,
          provider: { "@id": `${origin}/#business` },
          areaServed: AREAS.map((a) => a.name),
          url: `${origin}/services/${slug}`,
        });
      }
    } else if (pathname.startsWith("/service-areas/")) {
      const slug = pathname.split("/").pop();
      const area = AREAS.find((a) => a.slug === slug);
      if (area) {
        blocks.push({
          "@context": "https://schema.org",
          "@type": "Place",
          name: area.name,
          description: area.intro,
          url: `${origin}/service-areas/${slug}`,
          containedInPlace: { "@type": "State", name: "Florida" },
        });
      }
    }

    // Remove previous tags, inject fresh
    document.querySelectorAll('script[data-fp-jsonld="1"]').forEach((el) => el.remove());
    blocks.forEach((b) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.fpJsonld = "1";
      s.text = JSON.stringify(b);
      document.head.appendChild(s);
    });
  }, [pathname, lang]);

  return null;
}
